import {
    type AdobeCustomerModel,
    AdobeEventTypeEnum,
    type AdobeFormCompleteEventModel,
    type AdobeFormFieldErrorEventModel,
    type AdobeFormFieldInteractionEventModel,
    type AdobeFormStartEventModel,
    type AdobeInteractionClickEventModel,
    type AdobePageErrorEventModel,
    type AdobePageModel,
    type AdobePageViewEventModel,
    type AdobeProductsModel,
    type DataLayerCustomerModel,
    type DataLayerErrorModel,
    type DataLayerFinalModel,
    type DataLayerFormModel,
    type DataLayerJourneyModel,
    type DataLayerPageModel,
    type DataLayerProductModel,
    type DataLayerQuoteModel,
    type DataLayerStoreModel,
    EventTypeEnum,
    YesNoEnum,
} from "@/models";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { useCustomerStore } from "./customer.store";
import { SmokerStatusEnum, TitleEnum } from "@/enums";
import { useJourneyStore } from "./journey.store";
import { useQuoteStore } from "./quote.store";
import { getEnvironmentFromUrl } from "@/utils/url-utils";

const dlPageNames = [
    "Get a Quote",
    "Select Product",
    "Your Cover Summary",
    "Your Details",
    "Buy Now",
    "Cover Review Buy",
    "Thank You",
    "Rejected Page",
    "404 Error Page",
];

export const useDataLayerStore = defineStore("dataLayer", {
    state: (): DataLayerStoreModel => {
        return {
            pageNumber: 1,
            pageError: null,
            timestamp: dayjs(new Date()).format("DD/MM/YYYY HH:mm:ss ddd"),
            sessionCamID: "",
            adobeDataLayerEvents: [],
            errors: [],
        };
    },
    getters: {
        customer: () => {
            return useCustomerStore();
        },
        journey: () => {
            return useJourneyStore();
        },
    },
    actions: {
        adobeCustomerData(): AdobeCustomerModel {
            return {
                age: this.customer.age !== 0 ? this.customer.age : undefined,
                dateOfBirth: this.customer.fullDob,
                emailAddress: this.customer.email,
                gender:
                    this.customer.title === TitleEnum.Dr
                        ? "Unknown"
                        : this.customer.title === TitleEnum.Mr
                          ? "Male"
                          : "Female",
                forename: this.customer.firstname,
                ids: {
                    idol:
                        this.customer.customerID !== 1
                            ? this.customer.customerID
                            : undefined,
                },
                mobilePhoneNumber: this.customer.dayPhone,
                outcode: this.customer.address.postcode.split(" ")[0],
                postcode: this.customer.address.postcode,
                smokerStatus:
                    this.customer.smoker !== undefined
                        ? this.customer.smoker === SmokerStatusEnum.NoneAtAll
                            ? "non-smoker"
                            : "smoker"
                        : undefined,
                surname: this.customer.surname,
                type: "consumer",
            };
        },
        adobePageData(): AdobePageModel {
            const environment = getEnvironmentFromUrl(
                document.location.hostname
            );
            return {
                appName: "Over50s",
                businessArea: "L&Gretail",
                environment,
                journey: {
                    name: "TSB",
                    optimiseSSKey: this.urlParam("sskey") || "",
                    route: "New",
                    syndication: "TSB",
                    agencyNo: this.journey.agencyNumber.toString(),
                    campaignCode: this.journey.campaign,
                    channel: "Partners",
                },
                pageName: dlPageNames[this.pageNumber - 1],
                pageTitle: dlPageNames[this.pageNumber - 1],
                pageType: "Application",
                responsiveState: this.getScreenProperties().responsiveState,
                url: window.location.href,
            };
        },
        adobeProductData(): AdobeProductsModel {
            const quoteStore = useQuoteStore();

            let premiumAnnual;
            let premiumMonthly;
            let coverAmount;

            if (quoteStore.myDeal > -1 && quoteStore.availableDeals.length) {
                premiumAnnual = +(
                    (quoteStore.availableDeals[quoteStore.myDeal]
                        ?.monthlyCost || 0) * 12
                ).toFixed(2);

                premiumMonthly =
                    +quoteStore.availableDeals[
                        quoteStore.myDeal
                    ]?.monthlyCost?.toFixed(2);

                coverAmount =
                    +quoteStore.availableDeals[
                        quoteStore.myDeal
                    ]?.insuredAmount?.toFixed(2);
            }

            return {
                quote: {
                    quoteID: quoteStore.premiumID || undefined,
                    quotePolicyReference: quoteStore.quoteID || undefined,
                },
                cover: {
                    coverAmount,
                    coverType:
                        this.customer.fbo === undefined ||
                        this.customer.fbo === false
                            ? "O50FIX"
                            : "O50FIXFBO",
                },
                premium: {
                    premiumAnnual,
                    premiumMonthly,
                    premiumFrequency: premiumMonthly ? "Monthly" : undefined,
                },
            };
        },
        adobePageViewEvent(): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.PageView,
                pageViews: {
                    value: 1,
                },
                customer:
                    dlPageNames[this.pageNumber - 1] === "Get a Quote"
                        ? undefined
                        : this.adobeCustomerData(),
                page: this.adobePageData(),
                products: [this.adobeProductData()],
                order:
                    dlPageNames[this.pageNumber - 1] === "Thank You"
                        ? {
                              paymentMethod: "MDD",
                              paymentRegularity: "Monthly",
                          }
                        : undefined,
            } as AdobePageViewEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobePageErrorEvent(): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.PageView, // internally this is pageViewError but L&G want it to be pageView
                pageViews: {
                    value: 1,
                },
                page: {
                    errorCode: this.pageError?.errorCode,
                    errorMessage: this.pageError?.errorMessage,
                    errorType: "Page",
                },
            } as AdobePageErrorEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobeInteractionClickEvent(
            eventAction: string,
            eventType: string = "ButtonClick"
        ): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.InteractionClick,
                interactionClicks: {
                    value: 1,
                },
                interaction: {
                    interactionName: eventAction,
                    interactionType: eventType,
                },
            } as AdobeInteractionClickEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobeFormFieldInteraction(eventAction: string): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.FormFieldInteraction,
                formFieldInteractions: {
                    value: 1,
                },
                form: {
                    formName: "TSB Over 50s Life Insurance Application",
                    formField: eventAction,
                    formStep: dlPageNames[this.pageNumber - 1],
                },
            } as AdobeFormFieldInteractionEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobeFormFieldErrorEvent(eventAction: string): void {
            if (!eventAction) {
                return;
            }
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.FormFieldError,
                fieldErrors: {
                    value: 1,
                },
                form: {
                    formName: "TSB Over 50s Life Insurance Application",
                    formField: eventAction,
                    formErrorMessage: this.errors.find(
                        (error) => error.$propertyPath === eventAction
                    )?.$message,
                    formErrorType: "Form",
                },
            } as AdobeFormFieldErrorEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobeFormStartEvent(): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.FormStart,
                formStarts: {
                    value: 1,
                },
                form: {
                    formName: "TSB Over 50s Life Insurance Application",
                },
            } as AdobeFormStartEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        adobeFormCompleteEvent(): void {
            const adobeDataLayerEvent = {
                event: AdobeEventTypeEnum.FormComplete,
                formCompletes: {
                    value: 1,
                },
                form: {
                    formName: "TSB Over 50s Life Insurance Application",
                },
                customer: this.adobeCustomerData(),
                order: {
                    paymentMethod: "MDD",
                    paymentRegularity: "Monthly",
                },
                products: [this.adobeProductData()],
            } as AdobeFormCompleteEventModel;
            this.adobeDataLayerEvents.push(adobeDataLayerEvent);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).adobeDataLayer) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).adobeDataLayer.push(adobeDataLayerEvent);
            }
        },
        triggerAdobeEvent(
            eventType: EventTypeEnum | AdobeEventTypeEnum,
            eventAction: string = ""
        ): void {
            switch (eventType) {
                case AdobeEventTypeEnum.PageView:
                    this.adobePageViewEvent();
                    break;
                case EventTypeEnum.ErrorEvent:
                case AdobeEventTypeEnum.PageViewError:
                    this.adobePageErrorEvent();
                    break;
                case EventTypeEnum.PageInteraction:
                case AdobeEventTypeEnum.InteractionClick:
                    this.adobeInteractionClickEvent(eventAction);
                    break;
                case EventTypeEnum.QuestComplete:
                case AdobeEventTypeEnum.FormFieldInteraction:
                    this.adobeFormFieldInteraction(eventAction);
                    break;
                case EventTypeEnum.ErrorLoad:
                case AdobeEventTypeEnum.FormFieldError:
                    this.adobeFormFieldErrorEvent(eventAction);
                    break;
                case AdobeEventTypeEnum.FormStart:
                    this.adobeFormStartEvent();
                    break;
                case AdobeEventTypeEnum.FormComplete:
                    this.adobeFormCompleteEvent();
                    break;
                default:
                    break;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((window as any).app) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).app.adobeDataLayer = this.adobeDataLayer();
            }
        },
        adobeDataLayer() {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return JSON.parse(JSON.stringify(this.adobeDataLayerEvents));
        },
        triggerEvent(eventType: EventTypeEnum, eventAction: string = ""): void {
            // Start - Old data layer
            if (
                ![EventTypeEnum.ErrorEvent, EventTypeEnum.ErrorLoad].includes(
                    eventType
                )
            ) {
                this[eventType] = eventAction;
                const customEvent = new CustomEvent(eventType);
                document.dispatchEvent(customEvent);
                this.timestamp = dayjs(new Date()).format(
                    "DD/MM/YYYY HH:mm:ss ddd"
                );
            }
            this.satelliteTrack(eventType);
            // End - Old data layer

            // trigger new Adobe Event
            this.triggerAdobeEvent(eventType, eventAction);
        },
        urlParam(name: string): string | 0 {
            const results = new RegExp(`[?&]${name}=([^&#]*)`).exec(
                window.location.href.toLowerCase()
            );
            if (results === null) {
                return "";
            }
            return decodeURI(results[1]) || 0;
        },
        satelliteTrack(eventType: EventTypeEnum | string) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (typeof (window as any)._satellite !== "undefined") {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/unbound-method
                (window as any).app.digitalData = this.digitalData;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any)._satellite.track(eventType, this.digitalData());
            }
        },
        getScreenProperties(): { responsiveState: string; resState: string } {
            const windowWidth = screen.width;
            if (windowWidth < 767) {
                return { responsiveState: "Mobile", resState: "XS" };
            } else if (windowWidth > 768 && windowWidth < 991) {
                return { responsiveState: "Tablet", resState: "SM" };
            } else if (windowWidth > 992 && windowWidth < 1199) {
                return { responsiveState: "Tablet", resState: "MD" };
            }
            return { responsiveState: "Desktop", resState: "LG" };
        },
        digitalData(): DataLayerFinalModel {
            const quoteStore = useQuoteStore();
            // Build the journey object
            const journey = {
                agencyNo: this.journey.agencyNumber,
                campaignCode: this.journey.campaign,
                channel: "Partners",
                name: "TSB",
                route: "New",
                syndication: "TSB",
                sskey: this.urlParam("sskey"),
            } as DataLayerJourneyModel;

            // Build the page object
            const page = {
                URL: document.location.href,
                appName: "Over50s",
                country: "uk",
                currency: "gbp",
                hostName: document.location.hostname,
                language: navigator.language,
                pageName: dlPageNames[this.pageNumber - 1],
                pageType: "Application",
                path: document.location.pathname + document.location.search,
                phoneNumber:
                    document.getElementById("contact-number")?.innerText,
                previousPagePath: document.referrer,
                resolution: `${screen.width}x${screen.height}`,
                sessionCamID: this.sessionCamID,
                siteSection: "Over50s Life Insurance",
                timestamp: this.timestamp,
            } as DataLayerPageModel;

            if (this.pageNumber > 0) {
                page.pageVariant = "standard";
            }

            if (this.pageInteraction !== "") {
                page.interaction = this.pageInteraction;
            }

            page.environment = getEnvironmentFromUrl(page.hostName);

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            page.orientation = screen.orientation?.type;

            const pageProperties = this.getScreenProperties();
            page.responsiveState = pageProperties.responsiveState;
            page.resState = pageProperties.resState;

            // Build the form object
            const form = {
                loadingModalDisplayed: this.showDelayModal
                    ? YesNoEnum.Yes
                    : YesNoEnum.No,
            } as DataLayerFormModel;
            if (this.questComplete !== "") {
                form.questionComplete = this.questComplete;
            }

            // Build the customer object
            const dataLayerCustomer = {
                marketing: {
                    email: this.customer.gdpr.email
                        ? YesNoEnum.Yes
                        : YesNoEnum.No,
                    online: this.customer.gdpr.personalMarketing
                        ? YesNoEnum.Yes
                        : YesNoEnum.No,
                    post: this.customer.gdpr.post
                        ? YesNoEnum.Yes
                        : YesNoEnum.No,
                    sms: this.customer.gdpr.sms ? YesNoEnum.Yes : YesNoEnum.No,
                    telephone: this.customer.gdpr.phone
                        ? YesNoEnum.Yes
                        : YesNoEnum.No,
                    contactUs:
                        this.customer.gdpr.email ||
                        this.customer.gdpr.personalMarketing ||
                        this.customer.gdpr.post ||
                        this.customer.gdpr.sms ||
                        this.customer.gdpr.phone,
                },
                NCEmail: this.customer.ncEmail,
                address: {
                    addressline1: this.customer.address.line1,
                    city: this.customer.address.line4,
                    postcode: this.customer.address.postcode,
                },
            } as DataLayerCustomerModel;

            if (this.customer.customerID !== 1) {
                dataLayerCustomer.customerNumber = this.customer.customerID;
            }
            if (this.customer.age > 0) {
                dataLayerCustomer.age = this.customer.age;
            }
            if (this.customer.email !== "") {
                dataLayerCustomer.email = this.customer.email;
            }
            if (this.customer.firstname !== "") {
                dataLayerCustomer.firstname = "####";
            }
            if (this.customer.title !== TitleEnum.Blank) {
                dataLayerCustomer.gender =
                    this.customer.title === TitleEnum.Mr ? "Male" : "Female";
            }
            if (this.customer.surname !== "") {
                dataLayerCustomer.lastName = "####";
            }
            if (this.customer.fbo !== undefined) {
                dataLayerCustomer.fbo = this.customer.fbo
                    ? YesNoEnum.Yes
                    : YesNoEnum.No;
            }
            if (this.customer.smoker !== undefined) {
                dataLayerCustomer.smoker =
                    this.customer.smoker === SmokerStatusEnum.YesRegularly
                        ? "Yes - Regularly"
                        : this.customer.smoker ===
                            SmokerStatusEnum.YesOccasionally
                          ? "Yes - Occasionally"
                          : "None at all";
            }
            if (this.customer.caciData !== null) {
                dataLayerCustomer.ocean = { ...this.customer.caciData };
                /* eslint-disable camelcase */
                dataLayerCustomer.ocean.match_attrs = {
                    ...this.customer.caciData?.match_attrs,
                };
                dataLayerCustomer.ocean.segment_info = {
                    ...this.customer.caciData?.segment_info,
                };
                /* eslint-disable camelcase */
            }
            if (
                this.customer.dayPhone !== "" &&
                this.customer.dayPhone !== undefined
            ) {
                dataLayerCustomer.dayphone = this.customer.dayPhone;
            }

            // Build the quote object
            const quote = {
                premiumFrequency: "Monthly",
                start: true,
            } as DataLayerQuoteModel;

            // Build the product object
            const product = {
                coverType: "O50FIX",
            } as DataLayerProductModel;

            if (
                this.customer.fbo === undefined ||
                this.customer.fbo === false
            ) {
                product.coverType = "O50FIX";
                quote.coverType = "O50FIX";
            } else {
                product.coverType = "O50FIXFBO";
                quote.coverType = "O50FIXFBO";
            }

            if (quoteStore.premiumID) {
                quote.Id = quoteStore.premiumID;
            }
            if (quoteStore.quoteID !== "") {
                quote.policyRef = quoteStore.quoteID;
            }

            if (quoteStore.myQuote > -1 && quoteStore.availableDeals.length) {
                quote.annualPremium = (
                    (quoteStore.availableDeals[quoteStore.myQuote]
                        ?.monthlyCost || 0) * 12
                ).toFixed(2);
                quote.cover =
                    quoteStore.availableDeals[
                        quoteStore.myQuote
                    ]?.insuredAmount.toFixed(2);
                quote.monthlyPremium =
                    quoteStore.availableDeals[
                        quoteStore.myQuote
                    ]?.monthlyCost.toFixed(2);
            }

            if (quoteStore.myDeal > -1 && quoteStore.availableDeals.length) {
                product.annualPremium = (
                    (quoteStore.availableDeals[quoteStore.myDeal]
                        ?.monthlyCost || 0) * 12
                ).toFixed(2);
                product.cover =
                    quoteStore.availableDeals[
                        quoteStore.myDeal
                    ]?.insuredAmount.toFixed(2);
                product.monthlyPremium =
                    quoteStore.availableDeals[
                        quoteStore.myDeal
                    ]?.monthlyCost?.toFixed(2);
            }

            // Build the product object
            const error = {
                errorMessage: [],
                errorType: this.errors.length > 0 ? "Form" : "",
            } as DataLayerErrorModel;

            if (this.errors.length > 0) {
                for (const thisError of this.errors) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (error.errorMessage as any[]).push(thisError.$message);
                }
            }

            if (this.pageError?.errorCode === 404) {
                error.errorType = "page";
                error.errorCode = this.pageError.errorCode;
                error.errorMessage =
                    "We've looked everywhere but we can't find the page you're looking for.";
            } else if (this.pageError?.errorCode !== undefined) {
                page.pageName = "Error-unexpected";
                error.errorCode = this.pageError.errorCode || 500;
                error.errorType = "Page";
                error.errorMessage = this.errors;
            }

            // Put it all together and ship it off!
            const dataLayer = {
                journey,
                page,
                customer: dataLayerCustomer,
                quote,
                products: product,
            } as DataLayerFinalModel;

            if (typeof form !== "undefined") {
                dataLayer.form = form;
            }
            if (error.errorType !== "") {
                dataLayer.error = error;
            }
            if (this.pageNumber > 0) {
                dataLayer.productProminence = true;
            }
            if (this.pageNumber > 5) {
                dataLayer.confirmation = { paymentType: "MDD" };
            }

            if (error.errorType === "Page") {
                delete dataLayer.customer;
                delete dataLayer.quote;
                delete dataLayer.products;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).digitalData = dataLayer;
            return dataLayer;
        },
    },
});
